import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { promptData } from '../../../data/data_prompts';
import './AdministrationPrompts.css';

function AdministrationPrompts() {
  const [promptSets, setPromptSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [promptSetToDelete, setPromptSetToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [promptSetToClone, setPromptSetToClone] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneType, setCloneType] = useState('liminal');
  const [cloning, setCloning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPromptSets();
  }, []);

  const fetchPromptSets = async () => {
    try {
      setLoading(true);
      const response = await promptData.getPromptSets();
      
      if (response && response.success) {
        const promptSetsWithSections = await Promise.all(
          (response.promptSets || []).map(async (promptSet) => {
            try {
              const sectionsResponse = await promptData.getPromptSetSections(promptSet.id);
              return {
                ...promptSet,
                sections: sectionsResponse && sectionsResponse.success ? sectionsResponse.sections || [] : []
              };
            } catch (err) {
              console.error(`Error fetching sections for prompt set ${promptSet.id}:`, err);
              return {
                ...promptSet,
                sections: []
              };
            }
          })
        );
        setPromptSets(promptSetsWithSections);
      } else {
        setError('Failed to fetch prompt sets');
      }
    } catch (err) {
      console.error('Error fetching prompt sets:', err);
      setError('Error loading prompt sets');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (promptSetId) => {
    navigate(`/administration/prompts/edit/${promptSetId}`);
  };

  const handleDeleteClick = (promptSet) => {
    setPromptSetToDelete(promptSet);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!promptSetToDelete) return;

    try {
      setDeleting(true);
      const response = await promptData.deletePromptSet(promptSetToDelete.id);
      
      if (response && response.success) {
        // Remove the deleted prompt set from the list
        setPromptSets(promptSets.filter(ps => ps.id !== promptSetToDelete.id));
        setShowDeleteModal(false);
        setPromptSetToDelete(null);
      } else {
        setError('Failed to delete prompt set');
      }
    } catch (err) {
      console.error('Error deleting prompt set:', err);
      setError('Error deleting prompt set');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPromptSetToDelete(null);
  };

  const handleCloneClick = (promptSet) => {
    setPromptSetToClone(promptSet);
    setCloneName(`${promptSet.name} copy`);
    setCloneType(promptSet.type);
    setShowCloneModal(true);
  };

  const handleCloneConfirm = async () => {
    if (!promptSetToClone || !cloneName.trim()) return;

    try {
      setCloning(true);
      const response = await promptData.clonePromptSet(promptSetToClone.id, cloneName.trim(), cloneType);
      
      if (response && response.success) {
        // Refresh the prompt sets list to show the new cloned set
        await fetchPromptSets();
        setShowCloneModal(false);
        setPromptSetToClone(null);
        setCloneName('');
        setCloneType('liminal');
      } else {
        setError('Failed to clone prompt set');
      }
    } catch (err) {
      console.error('Error cloning prompt set:', err);
      setError('Error cloning prompt set');
    } finally {
      setCloning(false);
    }
  };

  const handleCloneCancel = () => {
    setShowCloneModal(false);
    setPromptSetToClone(null);
    setCloneName('');
    setCloneType('liminal');
  };

  const handleCreateNew = () => {
    navigate('/administration/prompts/create');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getValidationStatusClass = (validated) => {
    switch (validated) {
      case 'valid':
      case 'partial-valid':
      case 'partial valid':
        return 'valid';
      case 'invalid':
        return 'invalid';
      default:
        return 'not-validated';
    }
  };

  const getValidationIcon = (validated) => {
    switch (validated) {
      case 'valid':
      case 'partial-valid':
      case 'partial valid':
        return 'check';
      case 'invalid':
        return 'alert-circle';
      default:
        return 'clock';
    }
  };

  const getValidationText = (validated) => {
    switch (validated) {
      case 'valid':
        return 'Valid';
      case 'partial-valid':
      case 'partial valid':
        return 'PARTIAL VALID';
      case 'invalid':
        return 'Invalid';
      default:
        return 'Not Validated';
    }
  };



  if (loading) {
    return (
      <div className="prompts-container">
        <div className="prompts-header">
          <h1>Prompt Sets</h1>
        </div>
        <div className="loading-state">
          <i className="ti ti-loader ti-spin"></i>
          <p>Loading prompt sets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prompts-container">
        <div className="prompts-header">
          <h1>Prompt Sets</h1>
        </div>
        <div className="error-state">
          <i className="ti ti-alert-circle"></i>
          <p>{error}</p>
          <button onClick={fetchPromptSets} className="retry-button">
            <i className="ti ti-refresh"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="prompts-container">
      <div className="prompts-header">
        <h1>Prompt Sets</h1>
        <p>Manage prompt sets and scripts for the AI coaching system</p>
      </div>
      
      <div className="prompts-actions">
        <button onClick={handleCreateNew} className="create-button">
          <i className="ti ti-plus"></i>
          Create New Prompt Set
        </button>
      </div>
      
      <div className="prompts-table-container">
        <table className="prompts-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Validation</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {promptSets.map((promptSet) => (
              <tr key={promptSet.id}>
                <td className="prompt-id">{promptSet.id}</td>
                <td className="prompt-name">{promptSet.name}</td>
                <td className="prompt-type">
                  <span className="type-badge">{promptSet.type}</span>
                </td>
                <td className="prompt-status">
                  <span className={`status-badge ${promptSet.active ? 'active' : 'inactive'}`}>
                    {promptSet.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="prompt-validation">
                  {promptSet.validated ? (
                    <span className={`validation-badge ${getValidationStatusClass(promptSet.validated)}`}>
                      <i className={`ti ti-${getValidationIcon(promptSet.validated)}`}></i>
                      {getValidationText(promptSet.validated)}
                    </span>
                  ) : (
                    <span className="validation-badge not-validated">
                      <i className="ti ti-clock"></i>
                      Not Validated
                    </span>
                  )}
                </td>
                <td className="prompt-created">{formatDate(promptSet.created_at)}</td>
                <td className="prompt-actions">
                  <button 
                    onClick={() => handleEditClick(promptSet.id)} 
                    className="edit-button"
                    title="Edit Prompt Set"
                  >
                    <i className="ti ti-edit"></i>
                  </button>
                  <button 
                    onClick={() => handleCloneClick(promptSet)} 
                    className="clone-button"
                    title="Clone Prompt Set"
                  >
                    <i className="ti ti-copy"></i>
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(promptSet)} 
                    className="delete-button"
                    title="Delete Prompt Set"
                  >
                    <i className="ti ti-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {promptSets.length === 0 && (
          <div className="empty-state">
            <i className="ti ti-message-circle"></i>
            <p>No prompt sets found</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="modal-header">
              <h3>Delete Prompt Set</h3>
            </div>
            <div className="modal-content">
              <p>
                Are you sure you want to delete the prompt set "<strong>{promptSetToDelete?.name}</strong>"?
              </p>
              <p className="warning-text">
                This action cannot be undone. All scripts associated with this prompt set will also be removed.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleDeleteCancel} 
                className="cancel-button"
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteConfirm} 
                className="delete-confirm-button"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="ti ti-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {showCloneModal && (
        <div className="modal-overlay">
          <div className="clone-modal">
            <div className="modal-header">
              <h3>Clone Prompt Set</h3>
            </div>
            <div className="modal-content">
              <p>
                Create a copy of the prompt set "<strong>{promptSetToClone?.name}</strong>"
              </p>
              <div className="form-group">
                <label htmlFor="clone-name">New Name:</label>
                <input
                  type="text"
                  id="clone-name"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  placeholder="Enter new name"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="clone-type">Type:</label>
                <select
                  id="clone-type"
                  value={cloneType}
                  onChange={(e) => setCloneType(e.target.value)}
                  className="form-input"
                >
                  <option value="liminal">Liminal</option>
                  <option value="template">Template</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleCloneCancel} 
                className="cancel-button"
                disabled={cloning}
              >
                Cancel
              </button>
              <button 
                onClick={handleCloneConfirm} 
                className="clone-confirm-button"
                disabled={cloning || !cloneName.trim()}
              >
                {cloning ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Cloning...
                  </>
                ) : (
                  <>
                    <i className="ti ti-copy"></i>
                    Clone
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdministrationPrompts; 